import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from '../../i18n';
import { api, ApiError } from '../../api';
import { router } from '../../router';
import { showToast } from '../../components/ui/toast';
import type { Column } from '../../components/ui/table';

interface StudentLite {
  id: string; first_name: string; last_name: string; email: string;
  status: string; profile_photo_url: string | null; tags: { id: string; name: string; color: string }[];
}

@customElement('cadence-students-page')
export class CadenceStudentsPage extends LitElement {
  @state() private students: StudentLite[] = [];
  @state() private total = 0;
  @state() private page = 1;
  @state() private pages = 1;
  @state() private search = '';
  @state() private status = '';
  @state() private loading = true;
  @state() private showCreate = false;
  @state() private form = { first_name: '', last_name: '', email: '', active: false };
  @state() private formError = '';
  @state() private saving = false;
  @state() private createdLink = '';
  private debounce = 0;

  static styles = css`
    :host { display: block; }
    .head { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: var(--space-4); flex-wrap: wrap; }
    h1 { margin: 0; font-size: 1.6rem; }
    .toolbar { display: flex; gap: 12px; margin-bottom: var(--space-4); flex-wrap: wrap; align-items: flex-end; }
    .toolbar cadence-input { min-width: 240px; flex: 1; }
    .pager { display: flex; align-items: center; gap: 12px; justify-content: flex-end; margin-top: var(--space-4); color: var(--color-text-secondary); font-size: 0.9rem; }
    .tags { display: flex; gap: 4px; flex-wrap: wrap; }
    .form { display: flex; flex-direction: column; gap: var(--space-4); }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
    .hint { color: var(--color-text-secondary); font-size: 0.8rem; margin: -8px 0 0; }
    .created { display: flex; flex-direction: column; gap: var(--space-3); }
    .created p { margin: 0; }
    .link-box { display: flex; gap: 8px; align-items: center; }
    .link-box input { flex: 1; font: inherit; font-size: 0.85rem; padding: 9px 12px; border-radius: var(--radius-md); border: 1px solid var(--color-border); background: var(--color-bg-secondary); color: var(--color-text-primary); }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.load();
  }

  private async load() {
    this.loading = true;
    const params = new URLSearchParams({ page: String(this.page), limit: '25' });
    if (this.search.length >= 2) params.set('search', this.search);
    if (this.status) params.set('status', this.status);
    try {
      const data = await api.get<{ students: StudentLite[]; total: number; page: number; pages: number }>(`/students?${params}`);
      this.students = data.students;
      this.total = data.total;
      this.pages = data.pages;
      this.page = data.page;
    } catch (e) {
      showToast((e as Error).message, 'error');
    } finally {
      this.loading = false;
    }
  }

  private onSearch(value: string) {
    this.search = value;
    clearTimeout(this.debounce);
    this.debounce = window.setTimeout(() => { this.page = 1; this.load(); }, 300);
  }

  private onStatus(value: string) { this.status = value; this.page = 1; this.load(); }
  private goPage(delta: number) { const next = this.page + delta; if (next >= 1 && next <= this.pages) { this.page = next; this.load(); } }

  private openCreate() {
    this.form = { first_name: '', last_name: '', email: '', active: false };
    this.formError = ''; this.createdLink = ''; this.showCreate = true;
  }

  private closeCreate() {
    this.showCreate = false; this.createdLink = '';
    this.form = { first_name: '', last_name: '', email: '', active: false };
  }

  private async create() {
    if (!this.form.first_name || !this.form.last_name || !this.form.email) {
      this.formError = t('students.create.required');
      return;
    }
    this.saving = true; this.formError = '';
    try {
      const student = await api.post<{ onboarding_token: string }>('/students', this.form);
      showToast(t('students.toast.created'), 'success');
      this.page = 1;
      await this.load();
      if (this.form.active && student?.onboarding_token) {
        // Show the onboarding link inline so the admin can hand it over right away.
        this.createdLink = `${window.location.origin}/onboarding/${student.onboarding_token}`;
      } else {
        this.closeCreate();
      }
    } catch (e) {
      this.formError = e instanceof ApiError ? e.message : t('students.toast.createFailed');
    } finally {
      this.saving = false;
    }
  }

  private async copyLink() {
    try { await navigator.clipboard.writeText(this.createdLink); showToast(t('students.toast.linkCopied'), 'success'); }
    catch { showToast(t('students.toast.copyFailed'), 'error'); }
  }

  private columns: Column<StudentLite>[] = [
    {
      key: 'last_name', label: t('students.list.cols.name'), sortable: true,
      render: (s) => html`<div style="display:flex;align-items:center;gap:12px"><cadence-avatar name=${`${s.first_name} ${s.last_name}`} src=${s.profile_photo_url ?? ''} size="32"></cadence-avatar><span>${s.first_name} ${s.last_name}</span></div>`,
    },
    { key: 'email', label: t('students.list.cols.email'), sortable: true },
    {
      key: 'status', label: t('students.list.cols.status'), sortable: true,
      render: (s) => html`<cadence-badge variant="status" color=${s.status === 'active' ? 'var(--color-success)' : 'var(--color-text-secondary)'}>${s.status}</cadence-badge>`,
    },
    {
      key: 'tags', label: t('students.list.cols.tags'),
      render: (s) => html`<div class="tags">${(s.tags ?? []).map((tag) => html`<cadence-badge color=${tag.color}>${tag.name}</cadence-badge>`)}</div>`,
    },
  ];

  render() {
    return html`
      <div class="head">
        <h1>${t('students.list.title')}</h1>
        <cadence-button @click=${() => this.openCreate()}>${t('students.list.addStudent')}</cadence-button>
      </div>

      <div class="toolbar">
        <cadence-input label=${t('students.list.searchLabel')} placeholder=${t('students.list.searchPlaceholder')} .value=${this.search}
          @input=${(e: CustomEvent) => this.onSearch(e.detail)}></cadence-input>
        <cadence-select label=${t('students.list.cols.status')} .value=${this.status} placeholder=${t('students.list.allStatuses')}
          .options=${[{ value: 'active', label: t('students.status.active') }, { value: 'inactive', label: t('students.status.inactive') }]}
          @change=${(e: CustomEvent) => this.onStatus(e.detail)}></cadence-select>
      </div>

      <cadence-table
        clickable
        .columns=${this.columns}
        .rows=${this.students}
        @row-click=${(e: CustomEvent) => router.navigate(`/admin/students/${e.detail.id}`)}
      ></cadence-table>

      <div class="pager">
        <span>${this.total === 1 ? t('students.list.countOne', { count: this.total }) : t('students.list.countOther', { count: this.total })}</span>
        <cadence-button variant="secondary" ?disabled=${this.page <= 1} @click=${() => this.goPage(-1)}>${t('students.list.prev')}</cadence-button>
        <span>${t('students.list.pageOf', { page: this.page, pages: this.pages })}</span>
        <cadence-button variant="secondary" ?disabled=${this.page >= this.pages} @click=${() => this.goPage(1)}>${t('students.list.next')}</cadence-button>
      </div>

      <cadence-modal ?open=${this.showCreate} heading=${t('students.list.addStudent')} @close=${() => this.closeCreate()}>
        ${this.createdLink
          ? html`
            <div class="created">
              <p>${t('students.create.sharePrompt')}</p>
              <div class="link-box">
                <input type="text" readonly .value=${this.createdLink} aria-label=${t('students.create.onboardingLinkAria')} @focus=${(e: Event) => (e.target as HTMLInputElement).select()} />
                <cadence-button variant="secondary" @click=${() => this.copyLink()}>${t('students.create.copy')}</cadence-button>
              </div>
              <p class="hint">${t('students.create.singleUseHint')}</p>
            </div>`
          : html`
            <div class="form">
              ${this.formError ? html`<p class="form-error" role="alert">${this.formError}</p>` : ''}
              <cadence-input label=${t('students.create.firstName')} .value=${this.form.first_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, first_name: e.detail })}></cadence-input>
              <cadence-input label=${t('students.create.lastName')} .value=${this.form.last_name} @input=${(e: CustomEvent) => (this.form = { ...this.form, last_name: e.detail })}></cadence-input>
              <cadence-input label=${t('students.create.email')} type="email" .value=${this.form.email} @input=${(e: CustomEvent) => (this.form = { ...this.form, email: e.detail })}></cadence-input>
              <cadence-checkbox label=${t('students.create.setActive')} ?checked=${this.form.active} @change=${(e: CustomEvent) => (this.form = { ...this.form, active: e.detail })}></cadence-checkbox>
              <p class="hint">${t('students.create.activeHint')}</p>
            </div>`}
        <div slot="footer">
          ${this.createdLink
            ? html`<cadence-button @click=${() => this.closeCreate()}>${t('students.create.done')}</cadence-button>`
            : html`
              <cadence-button variant="ghost" @click=${() => this.closeCreate()}>${t('common.actions.cancel')}</cadence-button>
              <cadence-button ?loading=${this.saving} @click=${() => this.create()}>${t('students.create.create')}</cadence-button>`}
        </div>
      </cadence-modal>
    `;
  }
}
